import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PortalLayout from '../components/PortalLayout';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import {
  MapPin,
  Mail,
  Phone,
  MessageSquare,
  Send,
  Calendar,
  Lock,
  CheckCircle,
  Video,
  FileText,
  Copy,
  DollarSign,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CandidateDetail = () => {
  const { candidateId } = useParams();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState(null);
  const [hasAccessPass, setHasAccessPass] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch candidate (no eslint-disable needed)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/portal/talent/candidate/${candidateId}`);

        if (cancelled) return;

        setCandidate(response.data?.candidate || null);
        setHasAccessPass(!!response.data?.has_access_pass);
      } catch (error) {
        console.error('Failed to load candidate:', error);
        toast.error('Failed to load candidate');
        navigate('/portal/talent');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (candidateId) run();

    return () => {
      cancelled = true;
    };
  }, [candidateId, navigate]);

  const canViewContact = useMemo(() => {
    return !!(hasAccessPass && candidate?.discoverable);
  }, [hasAccessPass, candidate]);

  const copyToClipboard = async (text, label) => {
    const value = String(text || '').trim();
    if (!value) {
      toast.error(`${label} not available`);
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard`);
    } catch (e) {
      // fallback
      try {
        const ta = document.createElement('textarea');
        ta.value = value;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success(`${label} copied to clipboard`);
      } catch (err) {
        toast.error('Failed to copy');
      }
    }
  };

  const handleSendOffer = () => {
    if (!hasAccessPass) {
      toast.error('GoTalent Access Pass required to send offers');
      navigate('/portal/talent/access');
      return;
    }
    navigate(`/portal/talent/offers/new?candidateId=${candidateId}`);
  };

  const handleInviteInterview = () => {
    if (!hasAccessPass) {
      toast.error('GoTalent Access Pass required to send interview invites');
      navigate('/portal/talent/access');
      return;
    }
    navigate(`/portal/talent/interviews/new?candidateId=${candidateId}`);
  };

  const handleMessage = () => {
    navigate(`/portal/talent/messages?candidateId=${candidateId}`);
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </PortalLayout>
    );
  }

  if (!candidate) {
    return (
      <PortalLayout>
        <div className="max-w-4xl mx-auto py-16 text-center">
          <p className="text-muted-foreground">Candidate not found.</p>
          <button
            onClick={() => navigate('/portal/talent')}
            className="mt-4 px-4 py-2 rounded-lg border border-border hover:bg-muted text-foreground"
          >
            Back to Search
          </button>
        </div>
      </PortalLayout>
    );
  }

  const locationText = `${candidate.location_suburb || ''}${candidate.location_state ? `, ${candidate.location_state}` : ''}`.trim();

  return (
    <PortalLayout>
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/portal/talent')}
          className="mb-6 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back to Search</span>
        </button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                      {candidate.display_name || 'Candidate'}
                    </h1>

                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>{locationText || 'Location not provided'}</span>
                    </div>

                    {candidate.willing_to_relocate && (
                      <Badge className="mt-2 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        Willing to Relocate
                      </Badge>
                    )}
                  </div>

                  {candidate.discoverable && (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Open to Offers
                    </Badge>
                  )}
                </div>

                {/* Work Preferences */}
                {Array.isArray(candidate.work_type_preferences) && candidate.work_type_preferences.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-foreground mb-2">Work Type Preferences</div>
                    <div className="flex flex-wrap gap-2">
                      {candidate.work_type_preferences.map((type) => (
                        <Badge key={type} className="capitalize">
                          {String(type).replace('_', '-')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Salary Expectations */}
                {candidate.salary_expectation_min && candidate.salary_expectation_max && (
                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-foreground">
                      ${(candidate.salary_expectation_min / 1000).toFixed(0)}k - ${(candidate.salary_expectation_max / 1000).toFixed(0)}k
                      {candidate.salary_unit === 'year' ? ' per year' : ' per hour'}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Skills */}
            {Array.isArray(candidate.skills) && candidate.skills.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Skills</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {candidate.skills.map((skill) => (
                      <Badge
                        key={skill}
                        className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Experience */}
            {candidate.experience_summary && (
              <Card>
                <CardHeader>
                  <CardTitle>Experience</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground whitespace-pre-line">{candidate.experience_summary}</p>
                </CardContent>
              </Card>
            )}

            {/* Education */}
            {candidate.education_summary && (
              <Card>
                <CardHeader>
                  <CardTitle>Education</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground whitespace-pre-line">{candidate.education_summary}</p>
                </CardContent>
              </Card>
            )}

            {/* Video Cover Letter */}
            {candidate.video_cover_letter_ref_id && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-green-600" />
                    Video Introduction
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video bg-black rounded-lg flex items-center justify-center">
                    <div className="text-white text-center">
                      <Video className="w-16 h-16 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Video playback - Integration pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Details */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {canViewContact ? (
                  <>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Email</div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-foreground">
                          {candidate.user?.email || 'Not provided'}
                        </span>
                        <button
                          onClick={() => copyToClipboard(candidate.user?.email, 'Email')}
                          className="ml-auto p-2 hover:bg-muted rounded"
                          aria-label="Copy email"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Phone</div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-foreground">{candidate.phone || 'Not provided'}</span>
                        <button
                          onClick={() => copyToClipboard(candidate.phone, 'Phone')}
                          className="ml-auto p-2 hover:bg-muted rounded"
                          aria-label="Copy phone"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <Lock className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm font-semibold text-foreground mb-2">Contact Details Locked</p>
                    <p className="text-xs text-muted-foreground mb-4">
                      Purchase a GoTalent Access Pass to view contact details
                    </p>
                    <button
                      onClick={() => navigate('/portal/talent/access')}
                      className="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700"
                    >
                      Unlock Access
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <button
                  onClick={handleMessage}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MessageSquare className="w-5 h-5" />
                  Message Candidate
                </button>

                <button
                  onClick={handleSendOffer}
                  disabled={!hasAccessPass}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {hasAccessPass ? (
                    <>
                      <Send className="w-5 h-5" />
                      Send Job Offer
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Send Offer (Locked)
                    </>
                  )}
                </button>

                <button
                  onClick={handleInviteInterview}
                  disabled={!hasAccessPass}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-border font-semibold rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {hasAccessPass ? (
                    <>
                      <Calendar className="w-5 h-5" />
                      Invite to Interview
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Interview (Locked)
                    </>
                  )}
                </button>
              </CardContent>
            </Card>

            {/* Documents */}
            {candidate.resume_ref_id && (
              <Card>
                <CardHeader>
                  <CardTitle>Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-3 border border-border rounded-lg hover:bg-muted transition-colors"
                    onClick={() => toast.message('Resume viewer not implemented yet')}
                  >
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">View Resume</span>
                  </button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default CandidateDetail;
